import {$qs} from '../../../@shared/ts/dom';
import {maybeGotoPage} from '../features/modal';
import {store} from '../store';
import {Carts} from './cartReducer';
import {PeachPayCustomer} from './peachPayCustomerReducer';

const billingForm = () => $qs<HTMLFormElement>('#pp-billing-form') ?? undefined;
const shippingForm = () => $qs<HTMLFormElement>('#pp-shipping-form') ?? undefined;
const additionalForm = () => $qs<HTMLFormElement>('#pp-additional-form') ?? undefined;

export const PeachPayOrder = {
	collectSelectedShipping() {
		const carts = store.getState().calculatedCarts;
		const selectedShippingMethodsRecord: Record<string, string> = {};

		for (const cartKey of Object.keys(carts)) {
			const cart = carts[cartKey];

			if (!cart) {
				continue;
			}

			for (const packageKey of Object.keys(cart.package_record ?? {})) {
				const packageRecord = cart.package_record[packageKey];

				if (!packageRecord) {
					continue;
				}

				selectedShippingMethodsRecord[packageKey] = packageRecord.selected_method;
			}
		}

		return selectedShippingMethodsRecord;
	},
	billing: {
		checkValidity() {
			return billingForm()?.checkValidity() ?? true;
		},
		async reportValidity() {
			await maybeGotoPage('billing');
			return billingForm()?.reportValidity() ?? true;
		},
		formData() {
			return new FormData(billingForm());
		},
	},
	shipping: {
		checkValidity() {
			return shippingForm()?.checkValidity() ?? true;
		},
		async reportValidity() {
			await maybeGotoPage('shipping');
			return shippingForm()?.reportValidity() ?? true;
		},
		formData() {
			return new FormData(shippingForm());
		},
	},
	shippingOptions: {
		checkValidity() {
			return !Carts.needsShipping() || Carts.anyShippingMethodsAvailable();
		},
		async reportValidity() {
			if (PeachPayOrder.shippingOptions.checkValidity()) {
				await maybeGotoPage('shipping');
				PeachPayCustomer.shipToDifferentAddress(true, true);
				return false;
			}

			return true;
		},
	},
	additional: {
		checkValidity() {
			return additionalForm()?.checkValidity() ?? true;
		},
		async reportValidity() {
			await maybeGotoPage('shipping');
			return additionalForm()?.reportValidity() ?? true;
		},
		formData() {
			return new FormData(additionalForm());
		},
	},
	/**
	 * Checks all forms for validity. Returns false if any form is invalid. If
	 * the form is not present then that form is considered valid.
	 */
	checkValidity(): boolean {
		if (!(billingForm()?.checkValidity() ?? true)) {
			return false;
		}

		if (!(shippingForm()?.checkValidity() ?? true)) {
			return false;
		}

		if (!PeachPayOrder.shippingOptions.checkValidity()) {
			return false;
		}

		if (!(additionalForm()?.checkValidity() ?? true)) {
			return false;
		}

		return true;
	},
	/**
	 * Checks all forms for validity and reports the first invalid form to the
	 *  customer.
	 */
	async reportValidity(): Promise<boolean> {
		if (!(billingForm()?.checkValidity() ?? true)) {
			await maybeGotoPage('billing');
			return billingForm()?.reportValidity() ?? true;
		}

		if (!(shippingForm()?.checkValidity() ?? true)) {
			await maybeGotoPage('shipping');
			return shippingForm()?.reportValidity() ?? true;
		}

		if (!PeachPayOrder.shippingOptions.checkValidity()) {
			await PeachPayOrder.shippingOptions.reportValidity();
		}

		if (!(additionalForm()?.checkValidity() ?? true)) {
			await maybeGotoPage('shipping');
			return additionalForm()?.reportValidity() ?? true;
		}

		return true;
	},
	/**
	 * Gathers all form data from the billing, shipping, and additional forms.
	 */
	formData() {
		const formData = new FormData(billingForm());

		const shippingFormData = new FormData(shippingForm());
		for (const [key, value] of shippingFormData.entries()) {
			formData.append(key, value);
		}

		const additionalFormData = new FormData(additionalForm());
		for (const [key, value] of additionalFormData.entries()) {
			formData.append(key, value);
		}

		return formData;
	},
};
